"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import {
  animate,
  motion,
  useInView,
  useReducedMotion,
} from "framer-motion";
import { useRef } from "react";
import { Play, SealCheck, Clock } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { BrowserFrame } from "./BrowserFrame";
import { PhoneFrame } from "./PhoneFrame";
import type { THeroMediaType } from "@/lib/sanity/types";

export interface IHeroSlideResolved {
  src: string;
  caption?: string;
  alt?: string;
}

export interface IHeroDemoRowResolved {
  role: string;
  percent: number;
}

export interface IHeroMediaProps {
  mediaType: THeroMediaType;
  /** Derived embed URL for click-to-play mode (null → poster only). */
  videoEmbedUrl?: string | null;
  /** Derived embed URL for autoplay-loop mode. */
  videoLoopUrl?: string | null;
  videoTitle?: string;
  videoMeta?: string;
  videoLabel?: string;
  posterUrl?: string;
  slides?: IHeroSlideResolved[];
  deviceImages?: { src: string; alt: string }[];
  overlayUrl?: string;
  overlayTitle?: string;
  overlayBody?: string;
  demoRows?: IHeroDemoRowResolved[];
  demoPoolCents?: number;
}

const DEMO_BAR_COLORS = [
  "var(--chart-2)",
  "var(--chart-1)",
  "var(--chart-4)",
  "var(--chart-3)",
  "var(--chart-5)",
];

function formatCents(cents: number): string {
  return (cents / 100).toLocaleString("en-CA", {
    style: "currency",
    currency: "CAD",
  });
}

function VideoPoster({
  videoEmbedUrl,
  videoTitle,
  videoMeta,
  videoLabel,
  posterUrl,
}: Pick<
  IHeroMediaProps,
  "videoEmbedUrl" | "videoTitle" | "videoMeta" | "videoLabel" | "posterUrl"
>): React.ReactElement {
  const [playing, setPlaying] = useState(false);

  return (
    <BrowserFrame label={videoLabel ?? "tippo.ca — product tour"} className="rounded-[22px]">
      {playing && videoEmbedUrl ? (
        <div className="relative aspect-[16/10] bg-black">
          <iframe
            src={videoEmbedUrl}
            title="Tippo product tour"
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 h-full w-full border-0"
          />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => videoEmbedUrl && setPlaying(true)}
          className="relative block aspect-[16/10] w-full cursor-pointer overflow-hidden bg-black p-0"
          aria-label={videoTitle ?? "Play the Tippo product tour"}
        >
          {posterUrl && (
            <Image
              src={posterUrl}
              alt=""
              fill
              className="object-cover opacity-85"
              sizes="(max-width: 1080px) 100vw, 600px"
            />
          )}
          <span className="absolute inset-0 bg-gradient-to-b from-mauve-shadow-950/10 to-mauve-shadow-950/80" />
          {videoEmbedUrl && (
            <span className="absolute left-1/2 top-1/2 grid h-[82px] w-[82px] -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-primary text-primary-foreground shadow-[0_18px_44px_-14px_rgba(0,0,0,.7)]">
              <Play weight="bold" className="ml-1 h-[30px] w-[30px]" aria-hidden />
            </span>
          )}
          <span className="absolute bottom-5 left-[22px] flex flex-col gap-1.5 text-left">
            <span className="font-sans text-[19px] font-bold leading-tight tracking-tight text-white">
              {videoTitle ?? "See a full tip split in 90 seconds"}
            </span>
            <span className="font-sans text-[13px] font-medium leading-none text-white/70">
              {videoMeta ?? "Watch the tour"}
            </span>
          </span>
        </button>
      )}
    </BrowserFrame>
  );
}

function Carousel({ slides }: { slides: IHeroSlideResolved[] }): React.ReactElement {
  const [active, setActive] = useState(0);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion || slides.length < 2) return;
    const timer = setInterval(
      () => setActive((a) => (a + 1) % slides.length),
      4200
    );
    return () => clearInterval(timer);
  }, [prefersReducedMotion, slides.length]);

  return (
    <div className="rounded-[22px] border border-border bg-card px-6 pb-5 pt-6 shadow-xl">
      <div className="relative aspect-[16/11] overflow-hidden rounded-[14px] bg-muted">
        {slides.map((slide, i) => (
          <div
            key={slide.src}
            role="img"
            aria-label={slide.alt ?? slide.caption ?? ""}
            className="absolute inset-0 transition-opacity duration-700"
            style={{ opacity: active === i ? 1 : 0 }}
          >
            <Image
              src={slide.src}
              alt=""
              fill
              className="object-cover object-top"
              sizes="(max-width: 1080px) 100vw, 560px"
            />
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center justify-between gap-4">
        <div className="font-sans text-sm font-semibold text-foreground">
          {slides[active]?.caption ?? ""}
        </div>
        <div className="flex gap-[7px]">
          {slides.map((slide, i) => (
            <button
              key={slide.src}
              type="button"
              onClick={() => setActive(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={cn(
                "h-[3px] cursor-pointer rounded-full border-0 p-0 transition-all duration-200",
                active === i ? "w-10 bg-primary" : "w-[26px] bg-foreground/20"
              )}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function CountUpAmount({ cents }: { cents: number }): React.ReactElement {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const prefersReducedMotion = useReducedMotion();
  const [display, setDisplay] = useState(prefersReducedMotion ? cents : 0);

  useEffect(() => {
    if (!inView) return;
    if (prefersReducedMotion) {
      setDisplay(cents);
      return;
    }
    const controls = animate(0, cents, {
      duration: 1.1,
      ease: "easeOut",
      onUpdate: (latest) => setDisplay(Math.round(latest)),
    });
    return () => controls.stop();
  }, [inView, cents, prefersReducedMotion]);

  return (
    <div
      ref={ref}
      className="mt-2 font-sans text-[44px] font-bold leading-none tracking-tight text-foreground tabular-nums"
    >
      {formatCents(display)}
    </div>
  );
}

function LiveDemo({
  demoRows,
  demoPoolCents,
}: {
  demoRows: IHeroDemoRowResolved[];
  demoPoolCents: number;
}): React.ReactElement {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const prefersReducedMotion = useReducedMotion();

  return (
    <div
      ref={ref}
      className="relative overflow-hidden rounded-[22px] border border-border bg-card p-6 shadow-xl sm:p-[26px]"
    >
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <div className="font-sans text-[11px] font-semibold uppercase leading-none tracking-[.08em] text-muted-foreground">
            Tonight&rsquo;s pool
          </div>
          <CountUpAmount cents={demoPoolCents} />
        </div>
        <span className="inline-flex items-center gap-[7px] rounded-full bg-primary/10 px-3 py-1.5 font-sans text-xs font-semibold leading-none text-primary">
          <SealCheck weight="duotone" className="h-4 w-4" aria-hidden />
          Finalized
        </span>
      </div>
      <div className="mt-5 flex flex-col gap-3">
        {demoRows.map((row, i) => {
          const amount = Math.round((demoPoolCents * row.percent) / 100);
          const color = DEMO_BAR_COLORS[i % DEMO_BAR_COLORS.length];
          return (
            <div
              key={row.role}
              className="grid grid-cols-[1fr_auto] items-center gap-3.5"
            >
              <div>
                <div className="flex items-center justify-between gap-2.5 font-sans text-[13.5px] font-medium leading-none text-foreground">
                  <span>{row.role}</span>
                  <span className="text-muted-foreground tabular-nums">
                    {row.percent}%
                  </span>
                </div>
                <div className="mt-[7px] h-2 overflow-hidden rounded-full bg-muted">
                  <motion.div
                    className="h-full rounded-full"
                    style={{ backgroundColor: color }}
                    initial={{ width: prefersReducedMotion ? `${row.percent}%` : "0%" }}
                    {...(inView && { animate: { width: `${row.percent}%` } })}
                    transition={{ duration: 0.9, delay: 0.15 + i * 0.08, ease: "easeOut" }}
                  />
                </div>
              </div>
              <div className="min-w-[82px] text-right font-sans text-[15px] font-semibold leading-none text-foreground tabular-nums">
                {formatCents(amount)}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-5 flex items-center gap-2 border-t border-border pt-4 font-sans text-[13px] font-medium leading-snug text-muted-foreground">
        <Clock weight="duotone" className="h-[17px] w-[17px] text-primary" aria-hidden />
        Split in 30 seconds — weighted by hours worked.
      </div>
    </div>
  );
}

/**
 * Configurable hero media module. `mediaType` comes from Sanity so the hero's
 * right-hand side can switch between video, carousel, live demo, device
 * mockups, or an image overlay without a deploy.
 */
export function HeroMedia(props: IHeroMediaProps): React.ReactElement {
  const {
    mediaType,
    videoLoopUrl,
    slides = [],
    deviceImages = [],
    overlayUrl,
    overlayTitle,
    overlayBody,
    demoRows = [],
    demoPoolCents = 0,
  } = props;

  switch (mediaType) {
    case "videoAutoplay":
      return videoLoopUrl ? (
        <div className="relative aspect-[16/10] overflow-hidden rounded-[22px] border border-border bg-black shadow-xl">
          <iframe
            src={videoLoopUrl}
            title="Tippo in motion"
            allow="autoplay; encrypted-media"
            allowFullScreen
            className="absolute inset-0 h-full w-full border-0"
          />
        </div>
      ) : (
        <VideoPoster {...props} />
      );

    case "carousel":
      return <Carousel slides={slides} />;

    case "liveDemo":
      return <LiveDemo demoRows={demoRows} demoPoolCents={demoPoolCents} />;

    case "device":
      return (
        <div className="relative flex justify-center gap-5">
          {deviceImages[0] && (
            <PhoneFrame
              src={deviceImages[0].src}
              alt={deviceImages[0].alt}
              width={262}
              showNotch
            />
          )}
          {deviceImages[1] && (
            <PhoneFrame
              src={deviceImages[1].src}
              alt={deviceImages[1].alt}
              width={220}
              className="mb-8 hidden self-end sm:block"
            />
          )}
        </div>
      );

    case "imageOverlay":
      return (
        <div className="relative aspect-[4/3] overflow-hidden rounded-[22px] border border-border shadow-xl">
          {overlayUrl && (
            <Image
              src={overlayUrl}
              alt={overlayTitle ?? ""}
              fill
              className="object-cover"
              sizes="(max-width: 1080px) 100vw, 600px"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-mauve-shadow-950/90" />
          <div className="absolute inset-x-6 bottom-6">
            <div className="font-sans text-2xl font-bold leading-tight tracking-tight text-white">
              {overlayTitle}
            </div>
            {overlayBody && (
              <div className="mt-2 font-sans text-sm font-medium leading-normal text-white/75">
                {overlayBody}
              </div>
            )}
          </div>
        </div>
      );

    case "videoPoster":
    default:
      return <VideoPoster {...props} />;
  }
}
